import {
  JUNGLE_BACKEND_PROD_DOMAIN,
  JUNGLE_CLOUDFLARE_BASE_URL,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getFirebaseAuthorizationHeaders } from "../utils/firebase-auth-headers";

export enum CreatorProgramType {
  PROGRAM_1 = "program_1",
  PROGRAM_2 = "program_2",
}

export const DEFAULT_CREATOR_PROGRAM = CreatorProgramType.PROGRAM_1;

export const PROGRAM_2_PAY_PER_VIDEO_MINIMUM_DOLLARS = 20;

type CreatorProgramInput = {
  program?: string | number | null;
  creator_program?: string | number | null;
  creatorProgram?: string | number | null;
  program_type?: string | number | null;
  pay_per_video?: number | string | null;
} | null | undefined;

export const getCreatorProgram = (creatorInput: CreatorProgramInput): CreatorProgramType => {
  const rawProgramValue =
    creatorInput?.program ??
    creatorInput?.creator_program ??
    creatorInput?.creatorProgram ??
    creatorInput?.program_type;

  const normalizedProgramValue =
    typeof rawProgramValue === "string" ? rawProgramValue.trim().toLowerCase() : rawProgramValue;

  if (
    normalizedProgramValue === CreatorProgramType.PROGRAM_2 ||
    normalizedProgramValue === "program_2" ||
    normalizedProgramValue === "program 2" ||
    normalizedProgramValue === "program2" ||
    normalizedProgramValue === "2" ||
    normalizedProgramValue === 2
  ) {
    return CreatorProgramType.PROGRAM_2;
  }

  if (normalizedProgramValue != null && normalizedProgramValue !== "") {
    return CreatorProgramType.PROGRAM_1;
  }

  // No program value stored at all. Fall back to the pay-rate business rule
  // (creators paid $20+/video are Program 2) so creators whose program fields
  // were wiped by the old worker update_creators_list/add_creator write path
  // (it rebuilt each entry from a fixed field list and dropped
  // program/creator_program) still display and pay out under the right
  // program. An explicitly stored program value always wins over the pay rate.
  const payPerVideoNumber = Number(creatorInput?.pay_per_video);
  if (!Number.isNaN(payPerVideoNumber) && payPerVideoNumber >= PROGRAM_2_PAY_PER_VIDEO_MINIMUM_DOLLARS) {
    return CreatorProgramType.PROGRAM_2;
  }

  return DEFAULT_CREATOR_PROGRAM;
};

// Account type controls how each creator's videos are filtered before being counted toward
// leaderboards and payouts. WATER (the legacy default) excludes any video whose caption
// contains #water. JUNGLE inverts the rule: only videos containing #jungle are counted, and
// #water remains a global blacklist on top of that.
export enum CreatorAccountType {
  WATER = "water",
  JUNGLE = "jungle",
}

export const DEFAULT_CREATOR_ACCOUNT_TYPE = CreatorAccountType.WATER;

type CreatorAccountTypeInput = {
  account_type?: string | null;
  accountType?: string | null;
} | null | undefined;

export const getCreatorAccountType = (creatorInput: CreatorAccountTypeInput): CreatorAccountType => {
  const rawAccountTypeValue = creatorInput?.account_type ?? creatorInput?.accountType;

  const normalizedAccountTypeValue =
    typeof rawAccountTypeValue === "string" ? rawAccountTypeValue.trim().toLowerCase() : rawAccountTypeValue;

  if (normalizedAccountTypeValue === CreatorAccountType.JUNGLE) {
    return CreatorAccountType.JUNGLE;
  }

  return DEFAULT_CREATOR_ACCOUNT_TYPE;
};

export interface CreatorVideo {
  id: string;
  url: string;
  caption: string;
  createTime: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  duration: number;
}

export interface CreatorData {
  name: string;
  username: string;
  tiktok_handle?: string;
  instagram_handle?: string;
  platform?: string;
  profileUrl: string;
  tiktokUrl?: string;
  instagramUrl?: string;
  videos?: CreatorVideo[];
  posts?: any[]; // Instagram posts have different structure
  totalViews: number;
  totalSaves?: number;
  totalComments: number;
  totalLikes: number;
  totalShares?: number;
  rank: number;
  // Platform-specific totals
  tiktokTotalViews?: number;
  tiktokTotalSaves?: number;
  tiktokTotalComments?: number;
  tiktokTotalLikes?: number;
  tiktokTotalShares?: number;
  instagramTotalLikes?: number;
  instagramTotalComments?: number;
  instagramTotalViews?: number;
  program?: CreatorProgramType | string;
  creator_program?: CreatorProgramType | string;
  account_type?: CreatorAccountType | string;
}

export interface CreatorContentResponse {
  creators: CreatorData[];
  totalCreators: number;
  totalVideos: number;
  apifyRunCost: number;
}

export interface LeaderboardData {
  week_id: string;
  date_range: {
    start: string;
    end: string;
  };
  creators: CreatorData[];
  total_creators: number;
  total_videos: number;
  fetched_at: string;
  api_cost: number;
}

export interface Creator {
  name: string;
  tiktok_handle?: string;
  instagram_handle?: string;
  email?: string;
  is_archived?: boolean;
  pay_per_video?: number;
  program?: CreatorProgramType | string;
  creator_program?: CreatorProgramType | string;
  account_type?: CreatorAccountType | string;
}

export interface CreatorsListData {
  creators: Creator[];
  profiles?: string[]; // For backward compatibility
  updated_at: string | null;
}

// Used by the admin "manually add a video" flow. Each preview row reflects
// one URL the admin pasted: whether we could parse it, whether we could
// fetch Apify metadata for it, and whether it already exists in the
// creator_videos collection (in which case the admin is effectively just
// re-credentialing it as manually-added).
export interface FetchedVideoMetadata {
  caption: string;
  views: number;
  likes: number;
  comments_count: number;
  created_at: string | null;
  creator_username: string | null;
}

export interface ManuallyAddedVideoPreview {
  url: string;
  platform: "tiktok" | "instagram" | null;
  video_id: string | null;
  normalized_url?: string;
  fetched_metadata: FetchedVideoMetadata | null;
  suggested_week_id: string | null;
  already_exists: boolean;
  existing_creator_name: string | null;
  parse_error: string | null;
}

// What the frontend sends back when confirming. Each video corresponds to one
// preview entry, but the admin may have edited the caption/views/week_id.
export interface ManuallyAddedVideoConfirmInput {
  url: string;
  platform: "tiktok" | "instagram";
  video_id: string;
  week_id: string;
  caption: string;
  views: number;
  likes: number;
  comments_count: number;
  created_at: string | null;
}

export interface ManuallyAddedVideoResult {
  video_id: string | null;
  success: boolean;
  message: string;
}

export const getWeeklyLeaderboard = async (weekId: string): Promise<LeaderboardData | null> => {
  try {
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/creator_automations/get_weekly_leaderboard`, {
      method: "POST",
      body: JSON.stringify({
        week_id: weekId,
      }),
      headers: {
        "Content-Type": "application/json",
        ...(await getFirebaseAuthorizationHeaders()),
      },
    });

    const data = await response.json();

    if (data.status === "success") {
      return data.data;
    } else {
      console.error(`Failed to fetch leaderboard for week ${weekId}:`, data.message);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching leaderboard for week ${weekId}:`, error);
    return null;
  }
};

export const getCreatorsList = async (): Promise<CreatorsListData | null> => {
  try {
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/creator_automations/get_creators_list`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await getFirebaseAuthorizationHeaders()),
      },
    });

    const data = await response.json();

    if (data.status === "success") {
      return data.data;
    } else {
      console.error("Failed to fetch creators list:", data.message);
      return null;
    }
  } catch (error) {
    console.error("Error fetching creators list:", error);
    return null;
  }
};

export const updateCreatorsList = async (creators: Creator[]): Promise<boolean> => {
  try {
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/creator_automations/update_creators_list`, {
      method: "POST",
      body: JSON.stringify({
        creators: creators,
      }),
      headers: {
        "Content-Type": "application/json",
        ...(await getFirebaseAuthorizationHeaders()),
      },
    });

    const data = await response.json();

    if (data.status === "success") {
      return true;
    } else {
      console.error("Failed to update creators list:", data.message);
      return false;
    }
  } catch (error) {
    console.error("Error updating creators list:", error);
    return false;
  }
};

export const addCreator = async (creator: Creator): Promise<boolean> => {
  try {
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/creator_automations/add_creator`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await getFirebaseAuthorizationHeaders()),
      },
      body: JSON.stringify({ creator }),
    });

    const data = await response.json();

    if (data.status === "success") {
      return true;
    } else {
      console.error("Failed to add creator:", data.message);
      return false;
    }
  } catch (error) {
    console.error("Error adding creator:", error);
    return false;
  }
};

export const removeCreator = async (name: string): Promise<boolean> => {
  try {
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/creator_automations/remove_creator`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await getFirebaseAuthorizationHeaders()),
      },
      body: JSON.stringify({ name }),
    });

    const data = await response.json();

    if (data.status === "success") {
      return true;
    } else {
      console.error("Failed to remove creator:", data.message);
      return false;
    }
  } catch (error) {
    console.error("Error removing creator:", error);
    return false;
  }
};

export const refetchCreatorData = async (): Promise<boolean> => {
  try {
    // Fetch data for the previous week
    const response = await fetch(
      `${JUNGLE_BACKEND_PROD_DOMAIN}/creator_automations/fetch_creator_data_for_previous_week`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await getFirebaseAuthorizationHeaders()),
        },
      }
    );

    const data = await response.json();

    if (data.status === "success") {
      return true;
    } else {
      console.error("Failed to refetch creator data:", data.message);
      return false;
    }
  } catch (error) {
    console.error("Error refetching creator data:", error);
    return false;
  }
};

export const fetchCreatorDataForWeek = async (weekId: string): Promise<boolean> => {
  try {
    // Fetch data for a specific week
    const response = await fetch(
      `${JUNGLE_BACKEND_PROD_DOMAIN}/creator_automations/fetch_creator_data_for_specific_week`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await getFirebaseAuthorizationHeaders()),
        },
        body: JSON.stringify({ week_id: weekId }),
      }
    );

    const data = await response.json();

    if (data.status === "success") {
      return true;
    } else {
      console.error(`Failed to fetch creator data for week ${weekId}:`, data.message);
      return false;
    }
  } catch (error) {
    console.error(`Error fetching creator data for week ${weekId}:`, error);
    return false;
  }
};

export const getCombinedCreatorData = async (): Promise<any | null> => {
  try {
    // Fetch existing data from Firebase without triggering automation
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/creator_automations/get_existing_creator_data`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await getFirebaseAuthorizationHeaders()),
      },
    });

    const data = await response.json();

    if (data.status === "success") {
      return data.data;
    } else {
      console.error("Failed to fetch existing creator data:", data.message);
      return null;
    }
  } catch (error) {
    console.error("Error fetching combined creator data:", error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Admin manual-add-video flow — step 1 (preview)
// Posts a list of admin-provided URLs and gets back per-URL details:
// parsed platform + video id, Apify-fetched metadata (if available), the
// week id we'd default to, and whether the video already exists in the
// creator_videos collection. No persistence happens server-side until the
// admin confirms in step 2 below.
//
// NOTE: these two endpoints live in the Python backend (Zeet /
// cbackend.jungleai.com), NOT in the Cloudflare worker — so they hit
// JUNGLE_BACKEND_PROD_DOMAIN directly, matching the still-in-Python
// fetch_creator_data_* endpoints above. The worker only handles the
// creator endpoints that were ported to TS and would 404 these.
// ---------------------------------------------------------------------------
export const previewManuallyAddedVideos = async (
  videoUrls: string[]
): Promise<ManuallyAddedVideoPreview[] | null> => {
  try {
    const response = await fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/creator_automations/preview_manually_added_videos`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await getFirebaseAuthorizationHeaders()) },
      body: JSON.stringify({ urls: videoUrls }),
    });
    const data = await response.json();
    if (data.status === "success") {
      return (data.previews as ManuallyAddedVideoPreview[]) ?? [];
    }
    console.error("Failed to preview manually added videos:", data.message);
    return null;
  } catch (error) {
    console.error("Error previewing manually added videos:", error);
    return null;
  }
};

// Admin manual-add-video flow — step 2 (confirm + persist)
// The backend writes each video into creator_videos and adds the id to the
// appropriate creator_weekly_data/{week_id} doc. Returns one result entry
// per video so the UI can toast successes and failures separately (e.g. a
// URL whose caption contained #water will be rejected even though the
// admin confirmed it).
export const addManuallyAddedVideos = async (
  creatorName: string,
  videosToAdd: ManuallyAddedVideoConfirmInput[]
): Promise<ManuallyAddedVideoResult[] | null> => {
  try {
    const response = await fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/creator_automations/add_manually_added_videos`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await getFirebaseAuthorizationHeaders()) },
      body: JSON.stringify({ creator_name: creatorName, videos: videosToAdd }),
    });
    const data = await response.json();
    if (data.status === "success") {
      return (data.results as ManuallyAddedVideoResult[]) ?? [];
    }
    console.error("Failed to add manually added videos:", data.message);
    return null;
  } catch (error) {
    console.error("Error adding manually added videos:", error);
    return null;
  }
};

// Called right after a new user document is created on signup. If the user's
// email matches an active creator, the backend comps them onto the Elite plan.
// The backend requires the caller's Firebase token to match the acted-on user
// (or an admin), and reads the email from the user's own doc (authoritative),
// so this can't be used to grant Elite to an arbitrary account. Best-effort:
// returns true only when the grant actually happened. Hits the Python backend
// directly (JUNGLE_BACKEND_PROD_DOMAIN), like the other still-in-Python
// creator endpoints.
export const grantElitePlanIfUserIsCreator = async (userId: string): Promise<boolean> => {
  try {
    const response = await fetch(
      `${JUNGLE_BACKEND_PROD_DOMAIN}/creator_automations/grant_elite_plan_if_user_is_creator`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getFirebaseAuthorizationHeaders()) },
        body: JSON.stringify({ user_id: userId }),
      }
    );
    // Guard against non-2xx responses (e.g. an HTML error page from a proxy)
    // so a misconfigured backend logs an error instead of being silently
    // indistinguishable from a genuine "not a creator" result.
    if (!response.ok) {
      console.error("Grant Elite endpoint returned non-OK status:", response.status);
      return false;
    }
    const data = await response.json();
    return data.status === "success" && data.granted === true;
  } catch (error) {
    console.error("Error granting Elite plan to creator on signup:", error);
    return false;
  }
};
